import {format} from 'date-fns';
import React, {useCallback, useEffect, useState} from 'react';
import {TouchableOpacity, View} from 'react-native';
import {IMessage, isSameDay, isSameUser, MessageProps as GCMessageProps} from 'react-native-gifted-chat';
import {useDispatch, useSelector} from 'react-redux';
import {images} from '../../../../../assets/icons';
import {chatbotActions} from '../../../../../redux/reducers/socialSupport/chatbot';
import {holderProfileSelector} from '../../../../../redux/selectors/profile';
import {GradientView} from '../../../../../shared/components/gradientView';
import {Markdown} from '../../../../../shared/components/markdown';
import {Text} from '../../../../../shared/components/text';
import {useStyles} from '../../../../../shared/hooks/styles';
import {ActionType, ChatbotAction, MessageFrom} from '../../../../../shared/models/api';
import {DeviceSize, useDeviceSize} from '../../../../../shared/theme/helpers';
import {useTheme} from '../../../../../shared/theme';
import {DayChip} from './DayChip';

interface MessageProps extends GCMessageProps<IMessage> {
  currentMessage: IMessage;
  onReplyPress: (params: ChatbotAction) => void;
  hasUserReplyAfter?: boolean;
}

const arePropsEqual = (prevProps: MessageProps, nextProps: MessageProps) => {
  return (
    prevProps.currentMessage?._id === nextProps.currentMessage?._id &&
    prevProps.previousMessage?._id === nextProps.previousMessage?._id &&
    prevProps.currentMessage?.text === nextProps.currentMessage?.text &&
    prevProps.onReplyPress === nextProps.onReplyPress &&
    prevProps.hasUserReplyAfter === nextProps.hasUserReplyAfter
  );
};

export const Message = React.memo(function Message({
  currentMessage,
  previousMessage,
  onReplyPress,
  hasUserReplyAfter = false,
}: MessageProps) {
  const [showReplies, setShowReplies] = useState(true);
  const deviceSize = useDeviceSize();
  const dispatch = useDispatch();

  const styles = useStyles(({theme, mediaOnly}) => ({
    messageContainer: {
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      marginBottom: 10,
    },
    message: {
      minWidth: 50,

      ...mediaOnly(DeviceSize.mobile, {maxWidth: '90%'}, {maxWidth: '75%'}),
    },
    username: {
      fontSize: 12,
      lineHeight: 24,
      fontWeight: '600',
    },
    bubble: {
      padding: 10,
      borderRadius: 14,
      minWidth: 50,
      width: '100%',
    },
    messageTime: {
      textAlign: 'right',
      fontSize: 10,
      lineHeight: 12,
      marginTop: 4,
      color: '#5B6F7B',
    },
    chipContainer: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      width: '100%',
      flexShrink: 1,
      flexGrow: 0,
      paddingVertical: 7,
    },
    chip: {
      borderRadius: 8,
      paddingVertical: 9,
      paddingHorizontal: 20,
      marginVertical: 5,
      marginRight: 10,
      shadowOpacity: 0.14,
      shadowOffset: {
        width: 0,
        height: 2,
      },
      shadowRadius: 4,
      elevation: 1,
    },
    chipText: {
      lineHeight: 30,
      ...theme.fonts.bodySmallLight,
    },
  }));

  const {firstName = '', lastName = ''} = useSelector(holderProfileSelector()) ?? {};
  const isUser = currentMessage.user._id === MessageFrom.USER;
  const isNewDay = !previousMessage || !isSameDay(currentMessage, previousMessage);
  const theme = useTheme();
  const isFirstInBlock = !isSameUser(currentMessage, previousMessage);
  const showAvatarAndName = isFirstInBlock || isNewDay;
  const quickReplies = currentMessage?.quickReplies;
  const isGeneralQuestion = (currentMessage as any).general === true;
  const name = isUser ? `${firstName} ${lastName}` : 'Уралочка Ассистент';

  const handleReplyPress = useCallback(
    ({id, label, action}: ChatbotAction) => {
      return () => {
        if (!currentMessage?.text && !isUser) {
          dispatch(chatbotActions.removeMessageLocally(currentMessage._id));
        }

        setShowReplies(false);
        onReplyPress({id, label, action});
      };
    },
    [onReplyPress, currentMessage, isUser],
  );

  useEffect(() => {
    if ((hasUserReplyAfter && showReplies && isGeneralQuestion) || quickReplies?.values.length === 0) {
      setShowReplies(false);
    }
  }, [hasUserReplyAfter, showReplies, isGeneralQuestion, quickReplies?.values]);

  return (
    <>
      <View
        style={[
          styles.messageContainer,
          {
            flexDirection: isUser ? 'row-reverse' : 'row',
          },
        ]}>
        <View style={{width: 32}}>
          {showAvatarAndName && isUser && (
            <images.User width={24} height={24} style={{marginLeft: 10}} color={theme.color.userIcon} />
          )}
          {showAvatarAndName && !isUser && (
            <images.Chatbot width={24} height={24} style={{marginRight: 10}} color={theme.color.chatbotIcon} />
          )}
        </View>

        <View style={{flex: 1}}>
          <View style={[styles.message, isUser ? {marginLeft: 'auto'} : null]}>
            {showAvatarAndName && <Text style={[styles.username, {textAlign: isUser ? 'right' : 'left'}]}>{name}</Text>}
            {currentMessage?.text && (
              <View
                style={[
                  styles.bubble,
                  {
                    borderTopLeftRadius: showAvatarAndName && !isUser ? 2 : 14,
                    borderTopRightRadius: showAvatarAndName && isUser ? 2 : 14,
                    marginLeft: isUser ? 'auto' : 0,
                    marginRight: !isUser ? 'auto' : 0,
                    backgroundColor: isUser ? '#e5f9ee' : '#fff',
                  },
                ]}>
                <Markdown
                  titleSize='small'
                  checkmarkSize='small'
                  textStyle={{
                    fontSize: theme.fonts.bodySmallLight.fontSize,
                    fontFamily: theme.fonts.bodySmallLight.fontFamily,
                    fontWeight: theme.fonts.bodySmallLight.fontWeight,
                    lineHeight: theme.fonts.bodySmallLight.lineHeight,
                    letterSpacing: theme.fonts.bodySmallLight.letterSpacing,
                    padding: 0,
                    margin: 0,
                  }}
                  linkStyle={{
                    // @ts-ignore
                    wordBreak: 'break-all',
                  }}
                  paragraphStyle={{marginBottom: 0, marginTop: 0}}
                  blockquoteStyle={{margin: 0}}>
                  {currentMessage.text}
                </Markdown>
                <Text style={styles.messageTime}>{format(new Date(currentMessage.createdAt), 'HH:mm')}</Text>
              </View>
            )}
          </View>

          {!isUser && showReplies && (
            <View style={styles.chipContainer}>
              {quickReplies?.values.map(({messageId, title, value}, index) => (
                <TouchableOpacity
                  key={messageId}
                  onPress={handleReplyPress({id: messageId, label: title, action: value as ActionType})}>
                  <GradientView
                    gradient={{
                      angle: 90,
                      colors: [index % 2 === 0 && deviceSize !== DeviceSize.mobile ? '#E2ECF0' : '#EFF4F6', '#E8F1E9'],
                    }}
                    style={styles.chip}>
                    <Text style={styles.chipText}>{title}</Text>
                  </GradientView>
                </TouchableOpacity>
              ))}
            </View>
          )}
        </View>
      </View>

      {isNewDay && <DayChip date={currentMessage.createdAt} />}
    </>
  );
},
arePropsEqual);
